import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Landmark, Globe2, FileDown, Rss } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AI_MODELS, AI_MODEL_LABELS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CountryProfile() {
  const { name } = useParams();
  const country = decodeURIComponent(name);
  const [profile, setProfile] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [liveSources, setLiveSources] = useState(null);
  const [model, setModel] = useState("gpt-5.4");
  const [lookupPaused, setLookupPaused] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/countries/${encodeURIComponent(country)}/profile`)
      .then((r) => setProfile(r.data))
      .catch(() => setProfile(null))
      .finally(() => setLoaded(true));
  }, [country]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setStreaming(true);
    setStreamText("");
    setLiveSources(null);
    try {
      const res = await fetch(`${API}/countries/${encodeURIComponent(country)}/profile/generate?model=${model}`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.sources) {
            setLiveSources(payload.sources);
            setLookupPaused(!!payload.lookup_paused);
          } else if (payload.delta) {
            acc += payload.delta;
            setStreamText(acc);
          } else if (payload.error) {
            throw new Error(payload.error);
          }
        }
      }
      toast.success(`Procurement profile for ${country} generated.`);
      load();
    } catch (e) {
      toast.error("Stream interrupted — recovering saved draft.");
      load();
    } finally {
      setStreaming(false);
    }
  };

  const content = streaming ? streamText : profile?.content;
  const sources = streaming ? liveSources : profile?.sources;
  const showPausedNote = streaming ? lookupPaused : profile?.lookup_paused;

  return (
    <div>
      <Link to="/countries" data-testid="back-to-countries-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0050A0] transition-colors duration-200 mb-6">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" /> All countries
      </Link>
      <div className="bg-[#00294F] p-8 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7DD3F5] mb-2">Procurement Intelligence Profile</p>
          <h1 className="font-serif text-4xl text-white" data-testid="country-profile-title">{country}</h1>
          {profile?.generated_at && !streaming && (
            <p className="text-xs text-slate-400 mt-2">
              Last updated {profile.generated_at.slice(0, 10)}
              {profile.model && <span> · {AI_MODEL_LABELS[profile.model] || profile.model}</span>}
            </p>
          )}
        </div>
        {!streaming && (
          <div className="flex flex-wrap gap-3 shrink-0 items-center">
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="profile-model-select" className="w-[190px] bg-white/10 border-white/30 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {profile?.content && (
              <a
                href={`${API}/countries/${encodeURIComponent(country)}/profile/pdf`}
                data-testid="download-profile-pdf-btn"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 text-sm font-semibold hover:bg-white hover:text-[#0050A0] transition-colors duration-200"
              >
                <FileDown className="w-4 h-4" aria-hidden="true" /> Download PDF
              </a>
            )}
            <button
              onClick={generate}
              data-testid="generate-profile-btn"
              className="inline-flex items-center gap-2 bg-[#009EDB] text-white px-6 py-3 text-sm font-semibold hover:bg-white hover:text-[#0050A0] transition-colors duration-200"
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" /> {profile ? "Refresh Deep Dive" : "Generate Deep Dive"}
            </button>
          </div>
        )}
      </div>

      {!loaded ? (
        <p className="text-slate-400 text-sm py-16 text-center">Loading…</p>
      ) : !content && !streaming ? (
        <div className="bg-white border border-slate-200 p-16 text-center" data-testid="profile-empty-state">
          <Landmark className="w-10 h-10 text-slate-300 mx-auto mb-5" aria-hidden="true" />
          <h3 className="font-serif text-2xl text-slate-900 mb-3">No profile generated yet</h3>
          <p className="text-sm text-slate-500 max-w-lg mx-auto leading-relaxed">
            Generate a deep-dive covering {country}'s procurement laws (latest status), past postal tender
            landscape, compliance risks, and where UN/global frameworks apply.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200">
          <div className="flex items-center gap-2 px-8 py-4 border-b border-slate-200 bg-slate-50 text-sm text-slate-600">
            <Globe2 className="w-4 h-4 text-[#0050A0]" aria-hidden="true" />
            {streaming ? (
              <span data-testid="profile-streaming-indicator" className="text-[#0050A0] font-medium">
                {!streamText ? `Running live law lookup for ${country}…` : `Researching live — compiling ${country}'s legal framework and tender history…`}
              </span>
            ) : (
              <span>Legal framework, past tenders & compliance — {country}</span>
            )}
          </div>
          {showPausedNote && (
            <div className="px-8 py-4 border-b border-amber-200 bg-amber-50 flex items-start gap-2" data-testid="lookup-paused-note">
              <Rss className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
              <p className="text-sm text-amber-800">
                <strong>Live law lookup auto-paused</strong> — the monthly search credit limit has been reached.
                This profile was drafted from the AI model's knowledge only. Lookups resume automatically next month.
              </p>
            </div>
          )}
          {sources && sources.length > 0 && (
            <div className="px-8 py-5 border-b border-slate-200 bg-[#F0F7FC]" data-testid="live-sources-panel">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#0050A0] font-semibold mb-3 flex items-center gap-2">
                <Rss className="w-3.5 h-3.5" aria-hidden="true" /> Live Web Sources — fetched just now
              </p>
              <ol className="space-y-1.5">
                {sources.map((s, i) => (
                  <li key={s.url} className="text-xs text-slate-600 truncate" data-testid={`live-source-${i}`}>
                    <span className="font-mono text-[#0050A0] mr-2">[{i + 1}]</span>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#0050A0] hover:underline transition-colors duration-200">{s.title}</a>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className={`doc-prose p-10 max-w-4xl ${streaming ? "doc-cursor" : ""}`} data-testid="profile-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ""}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}
